import { haversineKm } from "@/src/utils/distance/distanceCalc";
import type { LatLng } from "@/src/constant/serviceArea/serviceArea";
import type { OutletDetails } from "@/src/types/tenant";

/**
 * Which single outlet serves this buyer.
 *
 * The storefront no longer narrows its catalogue to that outlet — grouping does
 * that job now (`groupCatalog.dedupeToUnique`), showing every branch's products
 * with cross-branch copies collapsed. The serving outlet still decides three
 * things: the delivery badge, the cart's outlet context, and which branch's
 * combos are offered.
 *
 * Ranking uses STRAIGHT-LINE distance, computed here. The delivery badge's
 * label comes from the product service instead (`storeDeliveryTime.service.ts`),
 * which knows each store's own delivery time and how it delivers; this only
 * needs to know which of two outlets is nearer, and ordering survives the
 * crow-flies approximation. Doing it locally also avoids a request per outlet
 * before the grid can render its first card.
 *
 * Pure — no localStorage, no fetch. The buyer's location is passed in, so this
 * runs identically on the server and in a test.
 */

/** `OutletDetails.coordinates` is `[lat, lng]`; reject partial or non-finite pairs. */
function toLatLng(coords?: [number, number] | null): LatLng | null {
  if (!coords || coords.length < 2) return null;
  const [lat, lng] = coords;
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return { lat, lng };
}

/**
 * The outlet serving a buyer at `buyerCoords`.
 *
 * Falls back to `defaultOutletId` (the tenant's primary outlet) for a
 * single-outlet store, for a buyer we cannot locate, and when no outlet has
 * usable coordinates. That fallback is what makes the anonymous render
 * deterministic and therefore cacheable — every first-time visitor gets
 * byte-identical HTML.
 */
export function nearestOutletId(
  outlets: OutletDetails[],
  buyerCoords: LatLng | null,
  defaultOutletId: string
): string {
  if (outlets.length < 2 || !buyerCoords) return defaultOutletId;

  /* Every landing outlet is a candidate. There used to be a stock filter here,
   * skipping branches with no products of their own — Anuttama's Whitefield hub
   * exists with zero. Two things retired it.
   *
   * It is no longer DERIVABLE: portal-api now fetches the catalogue collapsed
   * (`unique=true`), so the payload carries one winner per product and every
   * winner for tenant 139 happens to sit on the primary branch. Reading stock
   * from that list would conclude the second branch is empty and pin every
   * buyer to the primary — including one standing next to the second branch.
   *
   * And it is no longer NEEDED: the filter existed because /shop/all-products
   * scoped its server query to this id, so an empty branch rendered
   * "Showing 0 of 0". A multi-branch store now queries the whole tenant, and a
   * single-outlet store has nothing to choose between. */
  // Strict `<` keeps the earliest outlet on a tie, so the result is stable for a
  // given input order. An outlet with no coordinates scores Infinity and can
  // never win, which also leaves `defaultOutletId` standing when none are known.
  let best = defaultOutletId;
  let bestKm = Infinity;
  for (const outlet of outlets) {
    const coords = toLatLng(outlet.coordinates);
    const km = coords
      ? haversineKm(buyerCoords.lat, buyerCoords.lng, coords.lat, coords.lng)
      : Infinity;
    if (km < bestKm) {
      bestKm = km;
      best = outlet.outletId;
    }
  }
  return best;
}
